import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AccountingOrgBar from "./AccountingOrgBar";
import { canLoadAccountingData } from "../useAccountingScope";
import { AMOUNT_KEYS, LINE_SIDES, POSTING_POLICY_EXAMPLES } from "../constants";

const emptyLine = () => ({
	account_id: "",
	side: "debit",
	amount_key: "amount",
	line_order: 0,
	memo: "",
	uses_payment_method: false,
});

const PostingTemplatesTab = ({
	requiresOrganizationSelection,
	sessionOrganization,
	organizationId,
	setOrganizationId,
	eventTypes,
	fetchAccounts,
	fetchPostingTemplates,
	savePostingTemplate,
	isSubmitting,
}) => {
	const [templates, setTemplates] = useState([]);
	const [accounts, setAccounts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [form, setForm] = useState({
		event_type: "",
		name: "",
		description: "",
		is_active: true,
		lines: [emptyLine()],
	});

	const canLoad = canLoadAccountingData(requiresOrganizationSelection, organizationId);

	const load = async () => {
		if (!canLoad) {
			setTemplates([]);
			setAccounts([]);
			return;
		}
		setLoading(true);
		setError("");
		const [templatesResult, accountsResult] = await Promise.all([
			fetchPostingTemplates(organizationId),
			fetchAccounts(organizationId),
		]);
		if (templatesResult.success) setTemplates(templatesResult.templates);
		else setError(templatesResult.message);
		if (accountsResult.success) setAccounts(accountsResult.accounts);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [organizationId, requiresOrganizationSelection]);

	const openEdit = (template) => {
		setForm({
			event_type: template.event_type,
			name: template.name || "",
			description: template.description || "",
			is_active: template.is_active !== false,
			lines: (template.lines || []).map((line, idx) => ({
				account_id: line.related_account,
				side: line.side,
				amount_key: line.amount_key,
				line_order: line.line_order ?? idx,
				memo: line.memo || "",
				uses_payment_method: Boolean(line.uses_payment_method),
			})),
		});
		setDialogOpen(true);
	};

	const openCreateForEvent = (eventType) => {
		const example = POSTING_POLICY_EXAMPLES.find((p) => p.event_type === eventType);
		setForm({
			event_type: eventType,
			name: example?.label || eventType.replace(/_/g, " "),
			description: example ? `Suggested policy: ${example.when}` : "",
			is_active: true,
			lines: example
				? example.lines.map((line, idx) => ({
						account_id: "",
						side: line.side,
						amount_key: line.amount_key,
						line_order: idx,
						memo: "",
						uses_payment_method: Boolean(line.uses_payment_method),
				  }))
				: [
						{ ...emptyLine(), amount_key: "gross", uses_payment_method: true },
						{ ...emptyLine(), side: "credit", line_order: 1 },
				  ],
		});
		setDialogOpen(true);
	};

	const openCreate = () => {
		const defaultEvent =
			eventTypes[0]?.event_type ||
			POSTING_POLICY_EXAMPLES[0]?.event_type ||
			"tenant_rent_collected";
		openCreateForEvent(defaultEvent);
	};

	const updateLine = (index, patch) => {
		setForm((f) => ({
			...f,
			lines: f.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)),
		}));
	};

	const addLine = () => {
		setForm((f) => ({
			...f,
			lines: [...f.lines, { ...emptyLine(), line_order: f.lines.length }],
		}));
	};

	const removeLine = (index) => {
		setForm((f) => ({
			...f,
			lines: f.lines.filter((_, i) => i !== index),
		}));
	};

	const handleSubmit = async () => {
		if (!form.name.trim() || !form.event_type) {
			setMessage("Name and event type are required.");
			return;
		}
		if (!form.lines.length) {
			setMessage("Add at least one template line.");
			return;
		}
		setMessage("");
		const payload = {
			event_type: form.event_type,
			name: form.name.trim(),
			description: form.description,
			is_active: form.is_active,
			lines: form.lines.map((line, idx) => ({
				account_id: Number(line.account_id),
				side: line.side,
				amount_key: line.amount_key,
				line_order: idx,
				memo: line.memo,
				uses_payment_method: Boolean(line.uses_payment_method),
			})),
		};
		if (requiresOrganizationSelection) {
			payload.related_organization = organizationId;
		}
		const result = await savePostingTemplate(payload);
		if (result.success) {
			setDialogOpen(false);
			load();
		} else {
			setMessage(result.message);
		}
	};

	const postableAccounts = accounts.filter((a) => a.is_postable !== false && a.is_active !== false);

	const configuredEventTypes = useMemo(
		() => new Set(templates.filter((t) => t.is_active !== false).map((t) => t.event_type)),
		[templates]
	);

	const policyCatalog = useMemo(() => {
		const fromApi = (eventTypes || []).map((et) => ({
			event_type: et.event_type,
			label: et.label || et.event_type,
			auto_posted: et.auto_posted,
			registered: et.registered,
		}));
		const known = POSTING_POLICY_EXAMPLES.map((p) => ({
			event_type: p.event_type,
			label: p.label,
			auto_posted: true,
			registered: true,
		}));
		const byType = new Map();
		[...known, ...fromApi].forEach((row) => {
			byType.set(row.event_type, { ...byType.get(row.event_type), ...row });
		});
		return Array.from(byType.values()).filter((row) => row.event_type !== "manual_entry");
	}, [eventTypes]);

	return (
		<Box>
			<Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
				<AccountingOrgBar
					requiresOrganizationSelection={requiresOrganizationSelection}
					organizationId={organizationId}
					onOrganizationChange={setOrganizationId}
					sessionOrganization={sessionOrganization}
				/>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={openCreate}
					disabled={!canLoad || isSubmitting}
				>
					Add policy
				</Button>
			</Box>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
			{message && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessage("")}>{message}</Alert>}

			{!canLoad ? (
				<Typography color="text.secondary">Select an organisation.</Typography>
			) : loading ? (
				<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
			) : (
				<>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
						Policy coverage
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
						Each auto-posted event needs an active posting template for this organisation. Missing
						policies log as failed and do not block the domain action.
					</Typography>
					<TableContainer component={Paper} sx={{ mb: 3 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Event</TableCell>
									<TableCell>Status</TableCell>
									<TableCell align="right">Action</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{policyCatalog.length === 0 ? (
									<TableRow>
										<TableCell colSpan={3} align="center">
											No event types available yet.
										</TableCell>
									</TableRow>
								) : (
									policyCatalog.map((row) => {
										const configured = configuredEventTypes.has(row.event_type);
										const existing = templates.find((t) => t.event_type === row.event_type);
										return (
											<TableRow key={row.event_type} hover>
												<TableCell>
													<Typography variant="body2" sx={{ fontFamily: "monospace" }}>
														{row.event_type}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{row.label}
													</Typography>
												</TableCell>
												<TableCell>
													<Chip
														size="small"
														label={configured ? "Configured" : "Missing"}
														color={configured ? "success" : "warning"}
														variant="outlined"
													/>
												</TableCell>
												<TableCell align="right">
													{existing ? (
														<Button size="small" onClick={() => openEdit(existing)}>
															Edit
														</Button>
													) : (
														<Button
															size="small"
															variant="outlined"
															onClick={() => openCreateForEvent(row.event_type)}
															disabled={isSubmitting}
														>
															Create
														</Button>
													)}
												</TableCell>
											</TableRow>
										);
									})
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
						Saved templates
					</Typography>
					<TableContainer component={Paper}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Event type</TableCell>
									<TableCell>Name</TableCell>
									<TableCell>Lines</TableCell>
									<TableCell>Payment method line</TableCell>
									<TableCell>Active</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{templates.length === 0 ? (
									<TableRow>
										<TableCell colSpan={6} align="center">No posting templates yet.</TableCell>
									</TableRow>
								) : (
									templates.map((row) => (
										<TableRow key={row.id} hover>
											<TableCell sx={{ fontFamily: "monospace" }}>{row.event_type}</TableCell>
											<TableCell>{row.name}</TableCell>
											<TableCell>{row.lines?.length ?? 0}</TableCell>
											<TableCell>
												{(row.lines || []).some((l) => l.uses_payment_method) ? "Yes" : "No"}
											</TableCell>
											<TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
											<TableCell align="right">
												<IconButton size="small" onClick={() => openEdit(row)}>
													<EditIcon fontSize="small" />
												</IconButton>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}

			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
				<DialogTitle>Posting policy</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						<FormControl fullWidth>
							<InputLabel>Event type</InputLabel>
							<Select
								label="Event type"
								value={form.event_type}
								onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))}
							>
								{policyCatalog.map((et) => (
									<MenuItem key={et.event_type} value={et.event_type}>
										{et.label || et.event_type}
									</MenuItem>
								))}
								{(eventTypes || [])
									.filter((et) => !policyCatalog.some((p) => p.event_type === et.event_type))
									.map((et) => (
										<MenuItem key={et.event_type} value={et.event_type}>
											{et.label || et.event_type}
										</MenuItem>
									))}
							</Select>
						</FormControl>
						<TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
						<TextField label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />

						<Typography variant="subtitle2">Policy lines</Typography>
						<Typography variant="caption" color="text.secondary">
							Turn on &quot;Use payment method&quot; for the cash/wallet line so the mapped payment-method account is used at post time.
						</Typography>
						{form.lines.map((line, index) => (
							<Box key={index} display="flex" gap={1} flexWrap="wrap" alignItems="center">
								<FormControl size="small" sx={{ minWidth: 180 }}>
									<InputLabel>Account</InputLabel>
									<Select
										label="Account"
										value={line.account_id}
										onChange={(e) => updateLine(index, { account_id: e.target.value })}
									>
										{postableAccounts.map((a) => (
											<MenuItem key={a.id} value={a.id}>
												{a.code} — {a.name}
												{line.uses_payment_method ? " (placeholder)" : ""}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small" sx={{ minWidth: 100 }}>
									<InputLabel>Side</InputLabel>
									<Select label="Side" value={line.side} onChange={(e) => updateLine(index, { side: e.target.value })}>
										{LINE_SIDES.map((s) => (
											<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small" sx={{ minWidth: 120 }}>
									<InputLabel>Amount key</InputLabel>
									<Select label="Amount key" value={line.amount_key} onChange={(e) => updateLine(index, { amount_key: e.target.value })}>
										{AMOUNT_KEYS.map((k) => (
											<MenuItem key={k.value} value={k.value}>{k.value}</MenuItem>
										))}
									</Select>
								</FormControl>
								<TextField
									size="small"
									label="Memo"
									value={line.memo}
									onChange={(e) => updateLine(index, { memo: e.target.value })}
									sx={{ flex: 1, minWidth: 120 }}
								/>
								<FormControlLabel
									control={
										<Switch
											size="small"
											checked={Boolean(line.uses_payment_method)}
											onChange={(e) => updateLine(index, { uses_payment_method: e.target.checked })}
										/>
									}
									label="Use payment method"
									sx={{ ml: 0 }}
								/>
								<IconButton size="small" onClick={() => removeLine(index)} disabled={form.lines.length <= 1}>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Box>
						))}
						<Button size="small" onClick={addLine}>Add line</Button>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? "Saving…" : "Save policy"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PostingTemplatesTab;
